import { FeatureResult, InitResponse } from '@growthbook/growthbook';
import { captureException } from '@sortlist-frontend/mlm/standalone';
import { matchesMultiple } from '@sortlist-frontend/utils';

import { config as defaultsConfig } from '../defaults';
import { getConfigOverrides } from './overrides';
import {
  AvailableFeatureNames,
  Domain,
  Experiment,
  FeatureFlagValue,
  isFeatureFlagWithExperiment,
  Market,
} from './types';

export const STAGING_GLOBAL_TLD: Domain = 'www.sortlist-test.com';
export const STAGING_ES_TLD: Domain = 'www-es.sortlist-test.com';
export const STAGING_NL_TLD: Domain = 'www-nl.sortlist-test.com';
export const STAGING_DE_TLD: Domain = 'www-de.sortlist-test.com';
export const STAGING_IT_TLD: Domain = 'www-it.sortlist-test.com';
export const STAGING_CH_TLD: Domain = 'www-ch.sortlist-test.com';
export const STAGING_FR_TLD: Domain = 'www-fr.sortlist-test.com';
export const STAGING_CA_TLD: Domain = 'www-ca.sortlist-test.com';
export const STAGING_US_TLD: Domain = 'www-us.sortlist-test.com';
export const STAGING_BE_TLD: Domain = 'www.sortlist-test.be';
export const STAGING_UK_TLD: Domain = 'www-uk.sortlist-test.com';

export const PROD_GLOBAL_TLD: Domain = 'www.sortlist.com';
export const PROD_FR_TLD: Domain = 'www.sortlist.fr';
export const PROD_ES_TLD: Domain = 'www.sortlist.es';
export const PROD_NL_TLD: Domain = 'www.sortlist.nl';
export const PROD_DE_TLD: Domain = 'www.sortlist.de';
export const PROD_IT_TLD: Domain = 'www.sortlist.it';
export const PROD_CH_TLD: Domain = 'www.sortlist.ch';
export const PROD_BE_TLD: Domain = 'www.sortlist.be';
export const PROD_CA_TLD: Domain = 'www.sortlist.ca';
export const PROD_US_TLD: Domain = 'www.sortlist.us';
export const PROD_UK_TLD: Domain = 'www.sortlist.co.uk';

// Pathnames that are not relevant for any experiment
export const nonRelevantPathnames = ['/robots.txt', '/sitemaps', '/_monitor', '/blog'].map(
  (pathname) => `${pathname}*/**`,
);

export function domainToMarket(domain: Domain): Market | undefined {
  // French domains
  if (domain === STAGING_FR_TLD || domain === PROD_FR_TLD) {
    return 'fr';
  }

  // Spanish domains
  if (domain === STAGING_ES_TLD || domain === PROD_ES_TLD) {
    return 'es';
  }

  // Dutch domains
  if (domain === STAGING_NL_TLD || domain === PROD_NL_TLD) {
    return 'nl';
  }

  // German domains
  if (domain === STAGING_DE_TLD || domain === PROD_DE_TLD) {
    return 'de';
  }

  // Italian domains
  if (domain === STAGING_IT_TLD || domain === PROD_IT_TLD) {
    return 'it';
  }

  // Swiss domains
  if (domain === STAGING_CH_TLD || domain === PROD_CH_TLD) {
    return 'ch';
  }

  // Canadian domains
  if (domain === STAGING_CA_TLD || domain === PROD_CA_TLD) {
    return 'ca';
  }

  // US domains
  if (domain === STAGING_US_TLD || domain === PROD_US_TLD) {
    return 'us';
  }

  // Belgian domains
  if (domain === STAGING_BE_TLD || domain === PROD_BE_TLD) {
    return 'be';
  }

  // UK domains
  if (domain === STAGING_UK_TLD || domain === PROD_UK_TLD) {
    return 'uk';
  }

  if (domain === PROD_GLOBAL_TLD || domain === STAGING_GLOBAL_TLD) {
    return 'global';
  }

  return 'global';
}

export const isPathnameActivatedForExperiment = (experiment: Experiment, requestUrl: string): boolean => {
  const url = new URL(requestUrl);
  const { pathname } = url;
  if (experiment.pathnames != null) return matchesMultiple(experiment.pathnames, pathname);
  if (experiment.pathnamesIgnore != null) return !matchesMultiple(experiment.pathnamesIgnore, pathname);
  return true;
};

export function getDomain(host: string | undefined) {
  return host?.includes('.') ? host.replace(/^[^.]+(?=\.)/, '') : 'localhost';
}

export function getValueFromEvaluatedFeature<T extends AvailableFeatureNames>(feature: FeatureResult<T | null>) {
  if (feature.source === 'experiment') {
    return feature.experimentResult?.key as FeatureFlagValue | undefined;
  }
  if (feature.source === 'force') {
    return feature.value as FeatureFlagValue | undefined;
  }
}

export function isValueActive(v: FeatureFlagValue | boolean) {
  if (typeof v === 'string') {
    return !v.endsWith('-c') && v !== 'control';
  }
  return v;
}

export const getConfiguredFeaturesWithExperiments = (requestUrl: string) => {
  const configuredExperiments: AvailableFeatureNames[] = [];
  const serverOnlyConfiguredExperiments: AvailableFeatureNames[] = [];

  const overrides = getConfigOverrides(requestUrl);

  for (const [feature, config] of Object.entries(defaultsConfig)) {
    /**
     * We only get the features that:
     * - have an assigned experiment
     * - are activated for this pathname (url)
     * - are not overridden:
     *    - params take precedence (any value)
     *    - environment / market determine if it should be considered
     */
    const isFeatureOverridden =
      overrides.configOverrideFromParams[feature as AvailableFeatureNames]?.defaultValue != null ||
      overrides.configOverrideFromEnvironment[feature as AvailableFeatureNames]?.defaultValue === false;

    if (
      isFeatureFlagWithExperiment(config) &&
      isPathnameActivatedForExperiment(config.experiment, requestUrl) &&
      !isFeatureOverridden
    ) {
      configuredExperiments.push(feature as AvailableFeatureNames);

      if (config.experiment.isSSR) {
        serverOnlyConfiguredExperiments.push(feature as AvailableFeatureNames);
      }
    }
  }

  return { configuredExperiments, serverOnlyConfiguredExperiments };
};

export function logInit(
  response: InitResponse | { source: 'sync' | 'payload'; error?: Error },
  context: 'worker' | 'client' | 'server',
) {
  if (response.error != null) {
    captureException(`[Growthbook Init error]: failed to initialise growthbook`, {
      extra: { error: response.error, source: response.source, context },
      tags: { source: response.source, context },
    });
  } else {
    // For debugging purposes
    // console.info(`[GB]: Initialised from ${response.source}`, { context });
  }
}
